import { useToast } from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient, skipToken } from "@tanstack/react-query";
import { useCallback } from "react";
import { Artist } from "../../../types";
import { postToggleFollow } from "../../../api/artists";
import { useUserData } from "../../../hooks/useUserData";
import { FUNNEL_TYPES } from "../../funnel/utils/contants";
import { FunnelContextArea } from "../../funnel/types";
import { t } from "i18next";
import { useAccountData } from "../../../hooks/useAccountData";
import { GET_ACCOUNT_ME_QUERY_KEY } from "../../auth/api/authApi";
import { setFunnelModalConfig } from "../../funnel/FunnelModal.tsx";
import {
  trackArtistFollow,
  trackArtistUnfollow,
} from "../../../analytics/events/engagement";
import { ReaderNavigationContext } from "../../../screens/reader/types";

export function useToggleArtistFollow(
  cacheKey: string,
  modalContext = FunnelContextArea.followArtist,
  navigationContext?: ReaderNavigationContext,
) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const accountData = useAccountData();
  const { data } = useQuery<Artist>({
    queryKey: [cacheKey],
    queryFn: skipToken,
    networkMode: "offlineFirst",
    staleTime: Infinity,
  });

  const updateArtistFavorites = useCallback(
    async (artistFavorites: number[]) => {
      await queryClient.setQueryData([GET_ACCOUNT_ME_QUERY_KEY], () => {
        return {
          data: {
            payload: {
              results: {
                ...accountData,
                artist_favorites: artistFavorites,
              },
            },
          },
        };
      });
    },
    [accountData],
  );

  const { mutate } = useMutation({
    mutationFn: () => {
      return postToggleFollow(data?.id ?? 0, data?.user_is_following, navigationContext);
    },
    onMutate: async () => {
      if (!data) return;

      const newFollowingState = !data.user_is_following;

      // Update artist favorites in account data
      if (newFollowingState) {
        updateArtistFavorites([
          ...(accountData?.artist_favorites ?? []),
          data.id,
        ]);
      } else {
        updateArtistFavorites(
          (accountData?.artist_favorites ?? []).filter((id) => id !== data.id),
        );
      }

      // Update artist cache
      await queryClient.setQueryData([cacheKey], {
        ...data,
        user_is_following: newFollowingState,
      });

      toast({
        status: "success",
        title: `${newFollowingState ? "Following" : "Unfollowed"} ${data.roman_name}`,
      });

      return {
        artistId: data.id,
        newFollowingState,
      };
    },
    onError: () => {
      // Revert cache changes on error
      queryClient.invalidateQueries({ queryKey: [cacheKey] });

      toast({
        status: "error",
        title: `Unable to ${data?.user_is_following ? "unfollow" : "follow"} ${data?.roman_name}`,
      });
    },
    onSuccess: (_, __, context) => {
      if (!context?.artistId) {
        return;
      }

      const source = String(modalContext ?? FunnelContextArea.default);
      if (context.newFollowingState) {
        trackArtistFollow({
          artistId: context.artistId,
          source,
        });
      } else {
        trackArtistUnfollow({
          artistId: context.artistId,
          source,
        });
      }
    },
  });

  const { userData } = useUserData();

  const handleToggleArtistFollow = useCallback(() => {
    // If user_is_following is set, the user is authenticated (API only sets
    // this for logged-in users) — skip the signup gate even if account/me
    // hasn't been cached yet (e.g. direct page load race).
    if (!userData && !data?.user_is_following) {
      setFunnelModalConfig({
        type: FUNNEL_TYPES.signup,
        context: {
          modalContext,
        },
        options: {
          onSuccess: () => {
            mutate();
          },
          welcomeText: t("components.auth.welcomeText.getNotified", {
            artist: data?.roman_name,
          }),
        },
      });
    } else {
      mutate();
    }
  }, [data, userData, mutate, modalContext]);

  return { artistData: data, handleToggleArtistFollow };
}
